import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";

const MaskedLine = ({ children, delay, className }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block ${className}`}
        initial={reduce ? false : { y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const { content, L } = useContent();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);

  const h = content?.hero;
  const l1 = h ? L(h.l1) : t.hero.l1;
  const l2 = h ? L(h.l2) : t.hero.l2;
  const sub = h ? L(h.sub) : t.hero.sub;
  const cta1 = h ? L(h.cta1) : t.hero.cta1;
  const cta2 = h ? L(h.cta2) : t.hero.cta2;

  return (
    <section ref={ref} className="relative bg-[#F7F3EC] overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 lg:pt-44 pb-16 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow-label mb-8"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-testid="hero-eyebrow"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-serif text-[#191713] text-5xl sm:text-6xl lg:text-[4.8rem] leading-[1.06] tracking-tight" data-testid="hero-headline">
            <MaskedLine delay={0.35}>{l1}</MaskedLine>
            <MaskedLine delay={0.5} className="text-[#A6853B] italic">{l2}</MaskedLine>
          </h1>

          <motion.p
            className="mt-8 text-base sm:text-lg text-[#191713]/65 max-w-xl leading-relaxed"
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
          >
            {sub}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col sm:flex-row gap-4"
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <Link to={h?.cta1_link || "/contact"} data-testid="hero-cta-book-call" className="btn-dark">
              {cta1} <ArrowRight size={16} />
            </Link>
            <Link to={h?.cta2_link || "/pricing"} data-testid="hero-cta-pricing" className="btn-outline-dark">
              {cta2}
            </Link>
          </motion.div>

          <motion.div
            className="mt-16 hidden lg:flex items-center gap-3 text-[#8A8378] text-[0.65rem] tracking-[0.3em] uppercase"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            aria-hidden="true"
          >
            <ArrowDown size={14} className="text-[#A6853B]" /> {t.hero.scroll}
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={reduce ? false : { opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div aria-hidden="true" className="absolute -top-4 -left-4 w-full h-full border border-[#A6853B]/40" />
            <img
              src={h?.image || ""}
              alt={h?.image_alt || "Website shown on devices"}
              className="relative w-full object-cover aspect-[4/3] bg-[#E9E0D0]"
              loading="eager"
              data-testid="hero-image"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
